// Port of api/fuel/app/classes/insights.php
// Insight engine orchestrator. Discovers and runs segment classes per section.

import Constants from 'offline/constants'
import Util from 'offline/util'
import DB from 'offline/db'
import Game from 'offline/model/game'
import User from 'offline/model/user'

// Section registries (populated by index.js files via require.context)
var sections = {}

// Per-segment timing side-channel. When `Insights._profile` is truthy,
// generate() pushes one entry per segment into `Insights._lastTiming`. Used
// by scripts/offline-profile-section.js. No behavior change when disabled.
//
// Server-side precomputed segment outputs (Step 2 of the offline engine
// architecture). Snapshot import populates `_precomputed` with the
// `precomputed_segments` block from the payload, keyed by full segment
// name (`section.segment`). When `generate()` encounters a registered
// segment whose name is in the map, it returns the precomputed payload
// directly instead of running the segment locally.
var Insights = {

	_profile: false,
	_lastTiming: [],
	_precomputed: {},

	// Replace the precomputed segment map. Called by snapshot importers.
	setPrecomputed: function(map) {
		Insights._precomputed = map || {}
	},


	// Register all segments for a section.
	// Called by each section's index.js.
	register: function(name, segments) {
		sections[name] = segments
	},

	// Port of Insights::generate()
	generate: function(type, owner, section, insight, detail, filter, vars) {

		var run = []
		var added = []
		var userIndex = []

		// Build user index
		if (type === Constants.TYPE_USER) {

			userIndex.push(owner.get_id())

		} else if (type === Constants.TYPE_GROUP) {

			var groupLinks = DB.query(
				'SELECT user_id FROM link_group_user WHERE group_id = ' + owner.get_id()
			)
			for (var gi = 0; gi < groupLinks.length; gi++) {
				userIndex.push(groupLinks[gi].user_id)
			}

		} else if (type === Constants.TYPE_BUDDIES) {

			var buddyLinks = DB.query(
				'SELECT user_id FROM link_buddies_user WHERE buddies_id = ' + owner.get_id()
			)
			for (var bi = 0; bi < buddyLinks.length; bi++) {
				userIndex.push(buddyLinks[bi].user_id)
			}

		}

		// Get all segments for this section
		var allSegments = sections[section] || []

		// Discover valid segments and resolve dependencies
		for (var si = 0; si < allSegments.length; si++) {
			var seg = allSegments[si]

			if (seg.valid(type, insight, detail)) {

				if (added.indexOf(seg.name()) === -1) {
					added.push(seg.name())
					run.push({ segment: seg, priority: seg.priority() })
				}

				// Add required dependencies
				var reqs = seg.requires()
				for (var ri = 0; ri < reqs.length; ri++) {
					var reqName = reqs[ri]
					if (added.indexOf(reqName) === -1) {
						// Find the required segment by name
						for (var fi = 0; fi < allSegments.length; fi++) {
							if (allSegments[fi].name() === reqName) {
								added.push(reqName)
								run.push({ segment: allSegments[fi], priority: allSegments[fi].priority() })
								break
							}
						}
					}
				}

			}
		}

		// Sort by priority descending (higher priority runs first)
		run = Util.sort_by_field(run, 'priority', 'desc')

		// Execute segments
		var insights = {}
		var data = {}
		var references = { games: [], users: [] }
		if (Insights._profile) Insights._lastTiming = []

		for (var i = 0; i < run.length; i++) {
			var entry = run[i]
			var segment = entry.segment
			var segmentName = (typeof segment.name === 'function' ? segment.name() : segment.name) || 'unknown'
			var fullName = section + '.' + segmentName
			var precomputed = Insights._precomputed[fullName]

			if (precomputed === undefined) segment.inject(data)

			var _profileStart = Insights._profile ? (typeof performance !== 'undefined' ? performance.now() : Date.now()) : 0
			var result
			if (precomputed !== undefined) {
				// Server-side precomputed payload. Skip run/inject/data/refs
				// entirely so the segment never has to walk cross-user data.
				result = precomputed
			} else {
				result = segment.run(type, owner, userIndex, detail, filter, vars || {})
			}
			if (Insights._profile) {
				var _profileEnd = (typeof performance !== 'undefined' ? performance.now() : Date.now())
				Insights._lastTiming.push({
					name: segmentName,
					ms: _profileEnd - _profileStart,
					precomputed: precomputed !== undefined
				})
			}
				// Shallow in-place merge: segments usually produce one or two
				// top-level keys (e.g. `bias`, `compare`, `disparities`).
				// A few sections intentionally compose the same top-level object
				// across multiple segments (`size.overview`), so merge plain
				// objects one level deep instead of overwriting them outright.
				if (result) {
					var resultKeys = Object.keys(result)
					for (var rkx = 0; rkx < resultKeys.length; rkx++) {
						var key = resultKeys[rkx]
						var nextValue = result[key]
						var currentValue = insights[key]
						if (
							currentValue &&
							nextValue &&
							typeof currentValue === 'object' &&
							typeof nextValue === 'object' &&
							!Array.isArray(currentValue) &&
							!Array.isArray(nextValue)
						) {
							var nextKeys = Object.keys(nextValue)
							for (var nk = 0; nk < nextKeys.length; nk++) {
								currentValue[nextKeys[nk]] = nextValue[nextKeys[nk]]
							}
						} else {
							insights[key] = nextValue
						}
					}
				}

			if (precomputed !== undefined) continue

			var segData = segment.data()
			var segDataKeys = Object.keys(segData)
			for (var dk = 0; dk < segDataKeys.length; dk++) {
				data[segDataKeys[dk]] = segData[segDataKeys[dk]]
			}

			var segRefs = segment.references()
			if (segRefs.games) {
				for (var rgi = 0; rgi < segRefs.games.length; rgi++) {
					if (references.games.indexOf(segRefs.games[rgi]) === -1) {
						references.games.push(segRefs.games[rgi])
					}
				}
			}
			if (segRefs.users) {
				for (var rui = 0; rui < segRefs.users.length; rui++) {
					if (references.users.indexOf(segRefs.users[rui]) === -1) {
						references.users.push(segRefs.users[rui])
					}
				}
			}
		}

		// Bulk-fetch referenced data
		var responseData = Insights.data(references)

		return {
			insights: insights,
			data: responseData
		}
	},

	// Port of Insights::data() - bulk fetch referenced games and users.
	// Hot path for sections with many references (e.g. games section
	// references the user's full game_index, ~1000 ids). Both Game.to_array()
	// and User.to_array(true) are simple field-rename projections, so we
	// inline the projection here and skip Model instantiation entirely.
	// Saves ~250ms on a section with 1000 references.
	data: function(references) {
		var data = { games: [], users: [] }

		if (references.games && references.games.length) {
			var seenGames = {}
			var uniqueGames = []
			for (var gi = 0; gi < references.games.length; gi++) {
				var gid = parseInt(references.games[gi], 10) || 0
				if (gid && !seenGames[gid]) { seenGames[gid] = true; uniqueGames.push(gid) }
			}

			if (uniqueGames.length) {
				var gameRows = DB.queryFast(
					'SELECT game_id, game_name FROM game WHERE game_id IN (' + uniqueGames.join(',') + ')'
				)
				var games = new Array(gameRows.length)
				for (var gri = 0; gri < gameRows.length; gri++) {
					games[gri] = { id: gameRows[gri].game_id, name: gameRows[gri].game_name }
				}
				data.games = games
			}
		}

		if (references.users && references.users.length) {
			var seenUsers = {}
			var uniqueUsers = []
			for (var ui = 0; ui < references.users.length; ui++) {
				var uid = parseInt(references.users[ui], 10) || 0
				if (uid && !seenUsers[uid]) { seenUsers[uid] = true; uniqueUsers.push(uid) }
			}

			if (uniqueUsers.length) {
				var userRows = DB.queryFast(
					'SELECT user_id, user_name, user_firstname, user_lastname, user_sortname, user_country, user_avatar' +
					' FROM user WHERE user_id IN (' + uniqueUsers.join(',') + ')'
				)
				var users = new Array(userRows.length)
				for (var uri = 0; uri < userRows.length; uri++) {
					var u = userRows[uri]
					var first = u.user_firstname || ''
					var last = u.user_lastname || ''
					var full = first ? (last ? (first + ' ' + last) : first) : last
					var name = u.user_name || ''
					users[uri] = {
						id: u.user_id,
						name: name,
						fullname: full,
						sortname: u.user_sortname || '',
						country: u.user_country || 0,
						slug: encodeURIComponent(name).replace(/\+/g, ' '),
						avatar: u.user_avatar || ''
					}
				}
				data.users = users
			}
		}

		return data
	}

}

var requireSection = require.context('./insights', true, /index\.js$/)
requireSection.keys().forEach(function(key) {
	var mod = requireSection(key)
	var section = mod.default || mod
	if (section && section.name && section.segments) {
		Insights.register(section.name, section.segments)
	}
})

export default Insights
